import React, { useState } from 'react';
import { ChantierCard } from '../components/ChantierCard';
import { chantiers, clients } from '../utils/mockData';
import { Search, SlidersHorizontal } from 'lucide-react';
import { Status, WorkType, Priority } from '../utils/types';
import { Button } from '../components/ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/auth';
export function ChantiersList() {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [filter, setFilter] = useState<Status | 'all'>('all');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [selectedWorkType, setSelectedWorkType] = useState<WorkType | 'all'>('all');
  const [selectedPriority, setSelectedPriority] = useState<Priority | 'all'>('all');
  const filteredChantiers = chantiers.filter(chantier => {
    const matchesFilter = filter === 'all' || chantier.status === filter;
    const matchesSearch = chantier.name.toLowerCase().includes(search.toLowerCase()) || chantier.city.toLowerCase().includes(search.toLowerCase());
    const matchesClient = selectedClient === 'all' || chantier.clientId === selectedClient;
    const matchesWorkType = selectedWorkType === 'all' || chantier.workType === selectedWorkType;
    const matchesPriority = selectedPriority === 'all' || chantier.priority === selectedPriority;
    return matchesFilter && matchesSearch && matchesClient && matchesWorkType && matchesPriority;
  });
  const canCreate = user?.role === 'admin' || user?.role === 'bureau';
  return <div className="min-h-screen bg-gray-50 pb-20 md:pb-6" data-id="element-360">
      {/* Mobile Header */}
      <div className="md:hidden sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-4 shadow-sm" data-id="element-361">
        <div className="flex items-center justify-between mb-3" data-id="element-362">
          <h1 className="text-xl font-bold text-gray-900" data-id="element-363">Chantiers</h1>
          {canCreate && <Button size="sm" onClick={() => navigate('/chantiers/new')} data-id="element-364">
              Nouveau
            </Button>}
        </div>

        {/* Search Bar */}
        <div className="relative mb-3" data-id="element-365">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-366" />
          <input type="text" placeholder="Rechercher..." className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1DBAB4] focus:border-transparent outline-none text-sm" value={search} onChange={e => setSearch(e.target.value)} data-id="element-367" />
        </div>

        {/* Filter Toggle */}
        <Button variant="outline" size="sm" className="w-full" leftIcon={<SlidersHorizontal size={16} data-id="element-369" />} onClick={() => setShowFilters(!showFilters)} data-id="element-368">
          Filtres{' '}
          {(filter !== 'all' || selectedClient !== 'all' || selectedWorkType !== 'all' || selectedPriority !== 'all') && '(actifs)'}
        </Button>

        {/* Filter Chips */}
        <AnimatePresence data-id="element-370">
          {showFilters && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="overflow-hidden" data-id="element-371">
              <div className="space-y-3 mt-3" data-id="element-372">
                {/* Status Filter */}
                <div data-id="element-373">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-374">
                    Statut
                  </label>
                  <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-375">
                    {(['all', 'en_cours', 'bloque', 'termine', 'facturable'] as const).map(status => <button key={status} onClick={() => setFilter(status)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${filter === status ? 'bg-[#1E2A3A] text-white shadow-sm' : 'bg-gray-100 text-gray-600 active:scale-95'}`} data-id="element-376">
                        {status === 'all' ? 'Tous' : status === 'en_cours' ? 'En cours' : status === 'bloque' ? 'Bloqué' : status === 'termine' ? 'Terminé' : 'Facturable'}
                      </button>)}
                  </div>
                </div>

                {/* Client Filter */}
                <div data-id="element-377">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-378">
                    Client
                  </label>
                  <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-id="element-379">
                    <option value="all" data-id="element-380">Tous les clients</option>
                    {clients.map(client => <option key={client.id} value={client.id} data-id="element-381">
                        {client.raisonSociale}
                      </option>)}
                  </select>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block page-container" data-id="element-382">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-383">
          <div data-id="element-384">
            <h1 className="heading-xl" data-id="element-385">Tous les Chantiers</h1>
            <p className="text-caption mt-1" data-id="element-386">
              {filteredChantiers.length} chantier
              {filteredChantiers.length > 1 ? 's' : ''}
            </p>
          </div>

          {canCreate && <Button onClick={() => navigate('/chantiers/new')} data-id="element-387">
              Nouveau chantier
            </Button>}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-388">
          <div className="relative flex-1" data-id="element-389">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} data-id="element-390" />
            <input type="text" placeholder="Rechercher un chantier..." className="input-base pl-10 w-full sm:w-64" value={search} onChange={e => setSearch(e.target.value)} data-id="element-391" />
          </div>

          <div className="flex flex-wrap items-center gap-2" data-id="element-392">
            {/* Status Filter */}
            <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-gray-300" data-id="element-393">
              {(['all', 'en_cours', 'bloque', 'termine', 'facturable'] as const).map(status => <button key={status} onClick={() => setFilter(status)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === status ? 'bg-[#1E2A3A] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-394">
                  {status === 'all' ? 'Tous' : status === 'en_cours' ? 'En cours' : status === 'bloque' ? 'Bloqué' : status === 'termine' ? 'Terminé' : 'Facturable'}
                </button>)}
            </div>

            {/* Client Filter */}
            <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-id="element-395">
              <option value="all" data-id="element-396">Tous les clients</option>
              {clients.map(client => <option key={client.id} value={client.id} data-id="element-397">
                  {client.raisonSociale}
                </option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Chantiers Grid */}
      <div className="px-4 md:page-container" data-id="element-398">
        {filteredChantiers.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-399">
            {filteredChantiers.map((chantier, index) => <motion.div key={chantier.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} data-id="element-400">
                <ChantierCard chantier={chantier} data-id="element-401" />
              </motion.div>)}
          </div> : <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300" data-id="element-402">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-403">
              <Search className="text-gray-400" size={32} data-id="element-404" />
            </div>
            <h3 className="text-lg font-medium text-gray-900" data-id="element-405">
              Aucun chantier trouvé
            </h3>
            <p className="text-gray-500 mt-1 text-sm" data-id="element-406">
              Essayez de modifier vos filtres de recherche.
            </p>
          </div>}
      </div>
    </div>;
}